import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { LEDGauge, LEDBar } from './DashboardWidgets';

const DashboardTab = ({ 
  kpiData, 
  riskIndices, 
  geopoliticalData, 
  analysis, 
  fontSize 
}) => {
  return (
    <div className="space-y-6">
      {/* Data Source Badge + GSDI */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 text-xs font-mono uppercase tracking-wider border ${
            kpiData?.data_source === 'EIA/WorldBank LIVE' 
              ? 'border-marine-green text-marine-green bg-marine-green/10' 
              : 'border-marine-amber text-marine-amber bg-marine-amber/10'
          }`}>
            {kpiData?.data_source || 'LOADING'}
          </span>
          {kpiData?.market_sentiment && (
            <span className={`px-3 py-1 text-xs font-mono uppercase ${
              kpiData.market_sentiment === 'elevated' 
                ? 'text-marine-amber border border-marine-amber' 
                : 'text-marine-green border border-marine-green'
            }`}>
              Marché: {kpiData.market_sentiment === 'elevated' ? 'SOUS TENSION' : 'NORMAL'}
            </span>
          )}
        </div>
        
        {/* GTI (GDELT) + GSDI Indicators */}
        <div className="flex items-center gap-6">
          {/* GTI - Geopolitical Tension Index */}
          {geopoliticalData && (
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="font-mono text-cyan-300 text-[11px] uppercase font-semibold">Tension Géopolitique</p>
                <p className="font-mono text-[10px] text-white/80">GDELT {geopoliticalData.alert_level}</p>
              </div>
              <div 
                className="w-14 h-14 rounded-full border-3 flex items-center justify-center"
                style={{
                  borderColor: geopoliticalData.alert_color || '#22c55e',
                  borderWidth: '3px',
                  boxShadow: `0 0 15px ${geopoliticalData.alert_color}40`
                }}
              >
                <span 
                  className="font-heading text-lg font-bold"
                  style={{ color: geopoliticalData.alert_color || '#22c55e' }}
                >
                  {Math.round(geopoliticalData.geopolitical_tension_index || 0)}
                </span>
              </div>
            </div>
          )}

          {/* GSDI Indicator */}
          {riskIndices && (
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="font-mono text-cyan-300 text-[11px] uppercase font-semibold">Disruption Globale</p>
                <p className="font-mono text-[10px] text-white/80">{riskIndices.level}</p>
              </div>
              <div 
                className="w-16 h-16 rounded-full border-4 flex items-center justify-center"
                style={{
                  borderColor: riskIndices.gsdi >= 75 ? '#ef4444' : riskIndices.gsdi >= 55 ? '#f97316' : riskIndices.gsdi >= 35 ? '#eab308' : '#22c55e',
                  boxShadow: `0 0 20px ${riskIndices.gsdi >= 75 ? 'rgba(239,68,68,0.4)' : riskIndices.gsdi >= 55 ? 'rgba(249,115,22,0.4)' : riskIndices.gsdi >= 35 ? 'rgba(234,179,8,0.4)' : 'rgba(34,197,94,0.4)'}`
                }}
              >
                <span 
                  className="font-heading text-xl font-bold"
                  style={{ color: riskIndices.gsdi >= 75 ? '#ef4444' : riskIndices.gsdi >= 55 ? '#f97316' : riskIndices.gsdi >= 35 ? '#eab308' : '#22c55e' }}
                >
                  {Math.round(riskIndices.gsdi)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* LED Gauges */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <LEDGauge 
          label="Exposition Pétrole" 
          value={kpiData?.france_oil_dependency_ormuz || kpiData?.oil_dependency_pct || 0} 
          unit="%" 
          maxValue={30} 
          thresholds={{ warning: 15, danger: 25 }}
          size={fontSize === 'xlarge' ? 'large' : fontSize === 'large' ? 'normal' : 'small'}
        />
        <LEDGauge 
          label="Exposition Gaz" 
          value={kpiData?.france_gas_dependency_ormuz || kpiData?.gas_dependency_pct || 0} 
          unit="%" 
          maxValue={20}
          thresholds={{ warning: 10, danger: 15 }}
          size={fontSize === 'xlarge' ? 'large' : fontSize === 'large' ? 'normal' : 'small'}
        />
        <LEDGauge 
          label="Réserves Strat." 
          value={kpiData?.french_strategic_reserves_days || kpiData?.strategic_reserves_days || 0} 
          unit="j" 
          maxValue={120}
          thresholds={{ warning: 70, danger: 50 }}
          size={fontSize === 'xlarge' ? 'large' : fontSize === 'large' ? 'normal' : 'small'}
        />
        <LEDGauge 
          label="Strait Risk Index" 
          value={riskIndices?.components?.sri || 0} 
          unit="" 
          maxValue={100}
          thresholds={{ warning: 60, danger: 80 }}
          size={fontSize === 'xlarge' ? 'large' : fontSize === 'large' ? 'normal' : 'small'}
        />
      </div>

      {/* Price Bars */}
      <div className="grid lg:grid-cols-2 gap-4">
        <LEDBar 
          label="Prix Brent ($/baril)" 
          currentValue={kpiData?.brent_price_current || kpiData?.brent_current || 85} 
          crisisValue={kpiData?.brent_price_crisis_estimate || kpiData?.brent_crisis || 150} 
          unit="$"
        />
        <LEDBar 
          label="Prix Gaz (€/MWh)" 
          currentValue={kpiData?.gas_price_current || kpiData?.gas_current || 45} 
          crisisValue={kpiData?.gas_price_crisis_estimate || kpiData?.gas_crisis || 120} 
          unit="€"
        />
      </div>

      {/* Exposure Level */}
      <div className="bg-black border-2 border-marine-slate/50 p-6 text-center">
        <p className="font-mono text-marine-cyan text-lg uppercase tracking-wider mb-4">Niveau d'Exposition France</p>
        <div 
          className="inline-block px-12 py-4 border-4 font-heading text-4xl font-bold tracking-widest"
          style={{
            borderColor: kpiData?.exposure_level === 'ÉLEVÉ' ? '#ef4444' : kpiData?.exposure_level === 'MODÉRÉ' ? '#f59e0b' : '#10b981',
            color: kpiData?.exposure_level === 'ÉLEVÉ' ? '#ef4444' : kpiData?.exposure_level === 'MODÉRÉ' ? '#f59e0b' : '#10b981',
            textShadow: `0 0 20px ${kpiData?.exposure_level === 'ÉLEVÉ' ? 'rgba(239,68,68,0.5)' : kpiData?.exposure_level === 'MODÉRÉ' ? 'rgba(245,158,11,0.5)' : 'rgba(16,185,129,0.5)'}`
          }}
        >
          {kpiData?.exposure_level || 'MODÉRÉ'}
        </div>
      </div>

      {/* Alerts */}
      {analysis?.alerts && (
        <div className="bg-black border-2 border-marine-slate/50 p-6">
          <h3 className="font-heading text-marine-cyan text-xl uppercase tracking-wider mb-4 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6" />
            Alertes
          </h3>
          <div className="space-y-3">
            {analysis.alerts.map((alert, idx) => (
              <div key={idx} className={`p-4 border-l-4 ${alert.level === 'HIGH' ? 'border-marine-red bg-marine-red/20' : alert.level === 'MEDIUM' ? 'border-marine-amber bg-marine-amber/20' : 'border-marine-green bg-marine-green/20'}`}>
                <p className="font-mono text-base font-bold text-white">{alert.title}</p>
                <p className="font-mono text-sm text-white/80 mt-1">{alert.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardTab;
